<template>
  <div
    :class="layoutClass"
    class="layout-default"
  >
    <WidgetsArea area="above-menu" />
    <div class="layout-default__header">
      <TheHeader />
    </div>
    <div class="layout-default__header-submenu">
      <HeaderSubmenu />
    </div>
    <WidgetsArea area="below-menu" />
    <div
      :class="classLayoutDefaultContent"
      class="layout-default__content"
    >
      <slot />
    </div>
    <BadgesContainer />
    <div class="layout-default__footer">
      <TheFooter />
    </div>
  </div>
</template>

<script lang="ts">
  import type { TContent } from '~/types/common';
  import TheHeader from '~/components/TheHeader/TheHeader.vue';
  import TheFooter from '@/components/TheFooter.vue';
  import WidgetsArea from '~/components/WidgetsArea/WidgetsArea.vue';
  import HeaderSubmenu from '~/components/HeaderSubmenu.vue';
  import { useWidgetsStore } from '~/store/widgets';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import { useDeviceStore } from '~/store/device';
  import BadgesContainer from '~/features/badge/BadgesContainer.vue';

  export default defineNuxtComponent({
    name: 'LayoutDefault',

    components: {
      BadgesContainer,
      TheHeader,
      HeaderSubmenu,
      TheFooter,
      WidgetsArea,
    },

    setup() {
      const headScripts = useWidgetsStore().scripts;

      const getTagFromContent = (content: TContent[], tagName: string): TContent[] => {
        return content.reduce((result: TContent[], currContent: TContent) => {
          if (currContent.type === tagName) {
            result.push(currContent);
          } else if (currContent.children.length) {
            result.push(...getTagFromContent(currContent.children, tagName));
          }

          return result;
        }, [] as TContent[]);
      };

      useHead({
        meta: getTagFromContent(headScripts, 'meta').map((meta) => meta.attributes),
        script: [
          ...getTagFromContent(headScripts, 'script').map((script) => ({
            async: true,
            src: script.attributes?.src,
          })),

          ...getTagFromContent(headScripts, '#script-content').map((script) => ({
            // Check if unescape and try catch are neccesary
            innerHTML: script.text,
            type: 'text/javascript',
          })),
        ],
      });
    },

    computed: {
      article() {
        return useSingleArticleStore().article;
      },

      deviceType() {
        return useDeviceStore().deviceType;
      },

      isBrandWidgets(): number {
        const belowMenuWidgetsByDevice = useWidgetsStore().belowMenuWidgets.filter(
          (widget: TContent) => {
            return widget.attributes?.deviceType.includes(this.deviceType);
          },
        );

        return belowMenuWidgetsByDevice.filter((widget: TContent) => {
          return (
            widget.type === 'bh-kiss__html' && widget.attributes?.title?.startsWith('adfox brand')
          );
        }).length;
      },

      layoutClass() {
        const className = 'layout-default';
        return { [`${className}--advertising`]: this.isAdvertising };
      },

      classLayoutDefaultContent() {
        return {
          'layout-default__content--brand':
            this.isBrandWidgets && this.article?.template !== 'broadside',
        };
      },

      isAdvertising(): boolean {
        return !!this.article?.is_advertising;
      },
    },
  });
</script>

<style lang="scss" scoped>
  .layout-default {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background-color: $mainBlack;

    :deep() {
      .default-article {
        margin-top: 4px;
      }
    }

    @include tablet {
      background-color: #f2f0f0;
    }

    &__header,
    &__header-submenu,
    &__footer {
      // * для брендирования, чтобы баннер не перекрывал контент
      z-index: 2;
    }

    &__header,
    &__footer {
      flex-shrink: 0;
    }

    &__header {
      position: sticky;
      top: 0;
      left: 0;
      width: 100%;
      z-index: 9999999;
    }

    &__content {
      display: flex;
      flex: 1 0 auto;
      position: relative;

      &--brand {
        @include tablet {
          margin-top: 224px;
        }
      }
    }

    &__footer {
      position: relative;
      z-index: 1;
    }
  }
</style>
